import type { GameRules, GameSelection } from '@urbanrats/contracts';

/**
 * Correlated worst-case liability of one round (P14). Selections are not independent: in Traffic
 * Pulse EXACT wins together with EXACT_LOW or EXACT_HIGH, so summing per-selection limits understates
 * nothing but a per-selection check alone misses the joint outcome. Here every distinct outcome of the
 * game is enumerated and the payouts of all selections winning together are added.
 */
export function outcomeSets(rules: GameRules): GameSelection[][] {
  switch (rules.kind) {
    case 'COUNT_BANDS':
      return [['UNDER'], ['EXACT', 'EXACT_LOW'], ['RANGE'], ['EXACT', 'EXACT_HIGH'], ['OVER']];
    case 'COUNT_BUCKETS':
      return rules.buckets.map((b) => [b.selection]);
    case 'DUEL':
      return [['LANE_A'], ['LANE_B'], ['TIE']];
    case 'FIRST_EVENT':
      return [...rules.groups.map((g) => [g.selection]), ['NO_EVENT']];
  }
}

export interface Position {
  /** Potential total return of all accepted bets on the selection (atomic, after caps). */
  payoutAtomic: bigint;
  stakeAtomic: bigint;
}

/** Largest total payout over the game's outcomes, and the outcome that produces it. */
export function worstCase(rules: GameRules, positions: ReadonlyMap<GameSelection, Position>): { outcome: GameSelection[]; payoutAtomic: bigint; netAtomic: bigint } {
  const stakes = [...positions.values()].reduce((s, p) => s + p.stakeAtomic, 0n);
  let best = { outcome: [] as GameSelection[], payoutAtomic: 0n, netAtomic: -stakes };
  for (const o of outcomeSets(rules)) {
    const pay = o.reduce((s, sel) => s + (positions.get(sel)?.payoutAtomic ?? 0n), 0n);
    if (pay > best.payoutAtomic || best.outcome.length === 0) best = { outcome: o, payoutAtomic: pay, netAtomic: pay - stakes };
  }
  return best;
}
